import { apiClient } from "@/services/apiClient";

export type BlogPostStatus = "draft" | "scheduled" | "published" | "archived";

export interface BlogTag {
  id: string;
  slug: string;
  name: string;
}

export interface BlogPostListItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  publishedAt: string | null;
  tags: BlogTag[];
}

export interface BlogPostAdmin extends BlogPostListItem {
  contentHtml: string;
  contentJson: Record<string, unknown>;
  seoTitle: string | null;
  seoDescription: string | null;
  ogImageUrl: string | null;
  status: BlogPostStatus;
  scheduledFor: string | null;
  authorId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListBlogPostsResponse {
  posts: BlogPostAdmin[];
  total: number;
  page: number;
  limit: number;
}

export interface ListBlogPostsParams {
  page?: number;
  limit?: number;
  status?: BlogPostStatus;
  tag?: string;
  search?: string;
}

export interface CreateBlogPostInput {
  title: string;
  slug?: string;
  excerpt?: string;
  contentHtml: string;
  contentJson: Record<string, unknown>;
  coverImageUrl?: string;
  coverImageAlt?: string;
  seoTitle?: string;
  seoDescription?: string;
  ogImageUrl?: string;
  tagSlugs?: string[];
  scheduledFor?: string;
}

export type UpdateBlogPostInput = Partial<CreateBlogPostInput>;

function buildQuery(params: ListBlogPostsParams): string {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set("page", String(params.page));
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.status) qs.set("status", params.status);
  if (params.tag) qs.set("tag", params.tag);
  if (params.search) qs.set("search", params.search);
  return qs.toString();
}

export function listAdminPosts(
  params: ListBlogPostsParams = {},
): Promise<ListBlogPostsResponse> {
  const qs = buildQuery(params);
  const url = qs ? `/admin/blog/posts?${qs}` : "/admin/blog/posts";
  return apiClient.get<ListBlogPostsResponse>(url).then((r) => r.data);
}

export function getAdminPost(id: string): Promise<BlogPostAdmin> {
  return apiClient.get<BlogPostAdmin>(`/admin/blog/posts/${id}`).then((r) => r.data);
}

export function createPost(input: CreateBlogPostInput): Promise<BlogPostAdmin> {
  return apiClient.post<BlogPostAdmin>("/admin/blog/posts", input).then((r) => r.data);
}

export function updatePost(
  id: string,
  input: UpdateBlogPostInput,
): Promise<BlogPostAdmin> {
  return apiClient.patch<BlogPostAdmin>(`/admin/blog/posts/${id}`, input).then((r) => r.data);
}

export function deletePost(id: string): Promise<void> {
  return apiClient.delete(`/admin/blog/posts/${id}`).then(() => undefined);
}

export function publishPost(id: string): Promise<BlogPostAdmin> {
  return apiClient
    .post<BlogPostAdmin>(`/admin/blog/posts/${id}/publish`)
    .then((r) => r.data);
}

export function unpublishPost(id: string): Promise<BlogPostAdmin> {
  return apiClient
    .post<BlogPostAdmin>(`/admin/blog/posts/${id}/unpublish`)
    .then((r) => r.data);
}

export function schedulePost(
  id: string,
  scheduledFor: string,
): Promise<BlogPostAdmin> {
  return apiClient
    .post<BlogPostAdmin>(`/admin/blog/posts/${id}/schedule`, { scheduledFor })
    .then((r) => r.data);
}

export function uploadBlogImage(file: File): Promise<{ url: string }> {
  const form = new FormData();
  form.append("image", file);
  return apiClient
    .post<{ url: string }>("/admin/blog/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
}

export function listBlogTags(): Promise<BlogTag[]> {
  return apiClient.get<BlogTag[]>("/admin/blog/tags").then((r) => r.data);
}

export type BlogDraftTone =
  | "informative"
  | "promotional"
  | "casual"
  | "technical";
export type BlogDraftLength = "short" | "medium" | "long";

export interface GenerateBlogDraftInput {
  topic: string;
  tone?: BlogDraftTone;
  length?: BlogDraftLength;
  audience?: string;
}

export interface GeneratedBlogDraft {
  title: string;
  excerpt: string;
  contentHtml: string;
  suggestedTags: string[];
  seoTitle: string;
  seoDescription: string;
}

export function generateBlogDraft(
  input: GenerateBlogDraftInput,
): Promise<GeneratedBlogDraft> {
  return apiClient
    .post<GeneratedBlogDraft>("/admin/blog/generate", input)
    .then((r) => r.data);
}

export type BlogCoverImageStyle = "photographic" | "illustration" | "minimal";

export interface GenerateBlogCoverImageInput {
  prompt: string;
  style?: BlogCoverImageStyle;
}

export function generateBlogCoverImage(
  input: GenerateBlogCoverImageInput,
): Promise<{ url: string }> {
  return apiClient
    .post<{ url: string }>("/admin/blog/generate-cover", input)
    .then((r) => r.data);
}

export function createBlogTag(name: string, slug?: string): Promise<BlogTag> {
  return apiClient
    .post<BlogTag>("/admin/blog/tags", { name, slug })
    .then((r) => r.data);
}
